import React, { useEffect, useReducer, useRef, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import DashboardContainer from "../../components/dashboard-container";
import TransferOrders from "../../components/transfer-orders";
import AutocompleteWithPagination from "../../components/autocomplete-with-pagination";
import buildQuery from "../../utils/query-builder";
import { transferOrderService } from "../../utils/services/transferOrderService";
import { clientService } from "../../utils/services/clientService";
import { transferOrderFilterInitialState } from "../../utils/filter-config";

/**
 * Transfer Orders — stock moved between a client's locations, pulled from
 * Pipe17 into control-tower (api/transfer-order). The status shown is Pipe17's
 * own value.
 *
 * Clients reach it under Data → Transfer Orders and see their own transfers.
 * Admins reach it from the side menu, see every client's transfers (with a
 * Client column), and pick a client to filter or to sync.
 */
function filterReducer(state, action) {
  switch (action.type) {
    case "SET_FILTER":
      return {
        ...state,
        columns: state?.columns.map((x) => (x?.key === action?.payload?.key ? { ...x, ...action.payload } : x)),
      };
    case "UPDATE_LIMIT":
      return { ...state, limit: action.payload };
    default:
      return state;
  }
}

const isAdminUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null")?.role === "admin";
  } catch (e) {
    return false;
  }
};

const TransferOrdersPage = () => {
  const isAdmin = isAdminUser();
  const ownClientId = isAdmin ? null : localStorage.getItem("client_id");
  const [filterData, dispatch] = useReducer(filterReducer, transferOrderFilterInitialState);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [dataCount, setDataCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [client, setClient] = useState(null); // admin's picked client
  const clientId = isAdmin ? client?._id || null : ownClientId;

  const fetchRows = async (signal) => {
    setLoading(true);
    try {
      const response = await transferOrderService.fetch(isAdmin ? clientId : null, buildQuery(filterData, page), {
        signal,
      });
      setRows(response?.data || []);
      setDataCount(response?.total || 0);
    } catch (error) {
      if (error?.name !== "CanceledError" && error?.code !== "ERR_CANCELED") {
        console.error("Failed to load transfer orders", error);
        setRows([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchRows(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterData, clientId]);

  const handleFilterChange = (newFilter) => {
    dispatch({ type: "SET_FILTER", payload: newFilter });
    setPage(0);
  };

  // The sync runs through Pipe17 provider -> rl-oms-be -> control-tower and
  // answers before any transfer has landed, so the list is re-read a few times
  // while they arrive. The ref keeps the timers on the latest fetchRows.
  const fetchRowsRef = useRef(fetchRows);
  fetchRowsRef.current = fetchRows;
  const refreshTimers = useRef([]);
  useEffect(() => () => refreshTimers.current.forEach(clearTimeout), []);

  const handleSync = async () => {
    if (!clientId) return;
    setSyncing(true);
    try {
      await transferOrderService.sync(clientId);
      toast.success("Transfer order sync started. They will appear here shortly.");
      setPage(0);
      refreshTimers.current.forEach(clearTimeout);
      refreshTimers.current = [5000, 15000, 30000].map((ms) => setTimeout(() => fetchRowsRef.current(), ms));
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not start the Pipe17 transfer order sync. Please try again.");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <DashboardContainer
      onChangeFilter={handleFilterChange}
      filterData={filterData}
      fields={filterData.columns}
      header={"Transfer Orders"}
      hideUploadButton
      hideAddButton
      onClickRefresh={() => fetchRows()}
    >
      {isAdmin && (
        <Box sx={{ width: 280, maxWidth: "100%", mb: 1 }}>
          <AutocompleteWithPagination
            selectedItem={client}
            onDataChange={(val) => {
              setClient(val || null);
              setPage(0);
            }}
            width="100%"
            optionKey={(c) => c?.business_name || c?.name || ""}
            backgroundColor="var(--bg-paper)"
            customLabel="Client (all clients when empty)"
            apiEndPoint={(limit, skip, search) => clientService.fetch(limit, skip, search, null, "Active")}
          />
        </Box>
      )}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TransferOrders
          data={rows}
          dataCount={dataCount}
          page={page}
          rowsPerPage={filterData.limit}
          setRowsPerPage={(e) => dispatch({ type: "UPDATE_LIMIT", payload: e })}
          setPage={setPage}
          onChangeFilter={handleFilterChange}
          filterData={filterData}
          onSync={handleSync}
          syncing={syncing}
          syncDisabledReason={clientId ? "" : "Choose a client to sync its transfer orders"}
          showClient={isAdmin && !clientId}
        />
      )}
    </DashboardContainer>
  );
};

export default TransferOrdersPage;
